// featureinfo.js

import Overlay from 'ol/Overlay.js';
import { getMap } from './mapInstance.js';
import { Style, Stroke, Fill, Text } from 'ol/style.js';
import VectorLayer from 'ol/layer/Vector';
import { setCommand, setStatus } from './mapUtils.js';
import { resetActiveCommandsOnly } from './resetApplication.js';
import { setActiveSidebarTool, setSidebarToolActive } from './dialogs.js';
import { translate } from './languageTranslator.js';


let infoActive = false;
let infoOverlay = null;
let lastHighlightedFeature = null;
let escListener = null;
let infoToolsInitialized = false;
let observerInitialized = false;


const highlightStyle = new Style({
  stroke: new Stroke({ color: '#ff8800', width: 3 }),
  fill: new Fill({ color: 'rgba(255,136,0,0.25)' }),
  text: new Text({ font: '12px sans-serif', fill: new Fill({ color: '#880' }) })
});

const INFO_FIELD_DEFINITIONS = [
  {
    keys: ['huisnummer', 'u104720huisnummer'],
    labelKey: 'feature_info.field.huisnummer'
  },
  {
    keys: ['openbare_ruimte', 'u104719openbare_ruimte'],
    labelKey: 'feature_info.field.openbare_ruimte'
  },
  {
    keys: ['gebruiksdoel', 'gebruiksdeol', 'u104718gebruiksdoel'],
    labelKey: 'feature_info.field.gebruiksdoel'
  },
  {
    keys: ['huisletter', 'u104721huisletter'],
    labelKey: 'feature_info.field.huisletter'
  },
  {
    keys: ['toevoeging', 'u104722toevoeging'],
    labelKey: 'feature_info.field.toevoeging'
  },
  {
    keys: ['postcode6_BAG', 'u104723postcode6_BAG'],
    labelKey: 'feature_info.field.postcode6_bag'
  },
  {
    keys: ['u108213Admin_name', 'u108213Admin_naam', 'name'],
    labelKey: 'feature_info.field.name'
  },
  {
    keys: ['u108212Aantal_VBO_BAG'],
    labelKey: 'feature_info.field.number_of_addresses'
  }
];

// Inject CSS for info popup
(function injectStyles(){
  const css = `
    #info-popup { position: absolute; z-index: 2000; background: none!important; border: none;
      padding: 8px; font-family: sans-serif; font-size: .9rem; display: flex;
      flex-direction: column; gap: 4px; pointer-events: none;
      visibility: hidden; white-space: normal; word-wrap: break-word; align-items: stretch;
    }
    #info-popup .info-row { display: grid; grid-template-columns: max-content auto; gap: 2px 8px; align-items: start; }
    #info-popup span.key { color: red; font-weight:600; text-align:left; }
    #info-popup span.key::after { content: ":"; margin-left:2px; }
    #info-popup span.value { color: blue; text-align:left; }
    .info-button.active { background-color:#0d6efd!important; color:#fff!important; }
  `;
  const style = document.createElement('style');
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
})();

export function observeInfoButtonAndInit() {
  if (observerInitialized) return;
  observerInitialized = true;

  const sidebar = document.getElementById('sidebar');
  if (!sidebar) {
    console.error('Sidebar not found');
    return;
  }

  const existingBtn = sidebar.querySelector('.info-button');
  if (existingBtn) {
    setupInfoTools();
    return;
  }

  const observer = new MutationObserver(() => {
    const btn = sidebar.querySelector('.info-button');
    if (!btn) return;

    observer.disconnect();
    setupInfoTools();
  });

  observer.observe(sidebar, { childList: true, subtree: true });
}


function ensureOverlay() {

  const map = getMap();
  if (!map) return;

  if (!infoOverlay) {
    let elt = document.getElementById('info-popup');
    if (!elt) {
      elt = document.createElement('div');
      elt.id = 'info-popup';
      elt.style.visibility = 'hidden';
      document.body.appendChild(elt);
    }
    infoOverlay = new Overlay({
      element: elt,
      autoPan: false,
      positioning: 'bottom-left',
      stopEvent: false,
      autoPanAnimation: { duration: 250 }
    });
    map.addOverlay(infoOverlay);
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function isPC6Layer(layer) {
  const semanticRole = String(layer?.get?.('semanticRole') || layer?.get?.('addressAidsRole') || '').toLowerCase();
  const title = String(layer?.get?.('title') || layer?.get?.('typeName') || '').toLowerCase();

  return semanticRole === 'pc6' || title.includes('pc6');
}

function getInfoFieldLabel(field, layer) {
  return translate(field.labelKey);
}

function renderFeatureInfo(feature, layer) {
  const props = feature?.getProperties?.() || {};
  const rows = INFO_FIELD_DEFINITIONS
    .map(field => {
      const value = field.keys
        .map(key => props[key])
        .find(candidate => candidate !== undefined && candidate !== null && candidate !== '');

      if (value === undefined || value === null || value === '') return null;

      return `
        <div class="info-row">
          <span class="key">${escapeHtml(getInfoFieldLabel(field, layer))}</span>
          <span class="value">${escapeHtml(value)}</span>
        </div>
      `;
    })
    .filter(Boolean);

  if (!rows.length) {
    return Object.entries(props)
      .filter(([key]) => key !== 'geometry')
      .map(([key, value]) => `
        <div class="info-row">
          <span class="key">${escapeHtml(key)}</span>
          <span class="value">${escapeHtml(value)}</span>
        </div>
      `)
      .join('');
  }

  return rows.join('');
}

function displayFeatureInfo(evt) {
  const map = getMap();
  if (!map) return;

  ensureOverlay();
  const elt = infoOverlay.getElement();
  if (!elt) return;

  const hit = map.forEachFeatureAtPixel(
    evt.pixel,
    (feature, layer) => ({ feature, layer }),
    {
      layerFilter: layer =>
        layer instanceof VectorLayer &&
        layer.getVisible?.() &&
        !layer.get?.('disabledByRights'),
      hitTolerance: 8
    }
  );
  const feat = hit?.feature || null;
  const hitLayer = hit?.layer || null;

  if (lastHighlightedFeature && lastHighlightedFeature !== feat) {
    lastHighlightedFeature.setStyle(undefined);
  }
  lastHighlightedFeature = feat;

  if (!feat) {
    infoOverlay.setPosition(undefined);
    elt.style.visibility = 'hidden';
    return;
  }

  feat.setStyle(highlightStyle);

  elt.innerHTML = renderFeatureInfo(feat, hitLayer);

  infoOverlay.setPosition(evt.coordinate);
  elt.style.visibility = 'visible';
}


function deactivateInfoMode() {
  const map = getMap();
  if (!map) return;

  infoActive = false;

  document.querySelector('.info-button')?.classList.remove('active');
  setSidebarToolActive('.info-button', false);
  map.getViewport().style.cursor = '';

  map.un('pointermove', pointerMoveHandler);
  map.un('click', displayFeatureInfo);

  if (infoOverlay) {
    infoOverlay.setPosition(undefined);
    infoOverlay.getElement().style.visibility = 'hidden';
  }

  if (lastHighlightedFeature) {
    lastHighlightedFeature.setStyle(undefined);
    lastHighlightedFeature = null;
  }

  if (escListener) {
    document.removeEventListener('keydown', escListener);
    escListener = null;
  }

}

document.addEventListener('application-reset-active-tools', () => {
  deactivateInfoMode();
});


function pointerMoveHandler(evt) {

    const map = getMap();
  if (!map) return;

  if (evt.dragging) return;
  const over = map.hasFeatureAtPixel(evt.pixel);
  map.getViewport().style.cursor = over ? 'help' : '';
}

export function setupInfoTools() {
  if (infoToolsInitialized) {
    return;
  }

  const map = getMap();
  if (!map) {
    return;
  }

  let btn = document.querySelector('.info-button');
  if (!btn) {
    console.warn('No .info-button element found');
    return;
  }

  infoToolsInitialized = true;

  const cleanBtn = btn.cloneNode(true);
  btn.parentNode.replaceChild(cleanBtn, btn);
  btn = cleanBtn;

  btn.addEventListener('click', () => {

  if (!infoActive) {
  resetActiveCommandsOnly({ preserveAddressAidsResults: true });
  infoActive = true;
  setActiveSidebarTool(btn, { lock: true });

  map.on('click', displayFeatureInfo);
  setCommand('Click on the feature to view feature info, ESC to cancel');
  setStatus('Feature Info activated');

  // ADD ESC LISTENER HERE
  escListener = (evt) => {
    if (evt.key === 'Escape') {
      deactivateInfoMode();
    }
  };
  document.addEventListener('keydown', escListener);

  } 
  else 
  {
    deactivateInfoMode();
  }

  });
}

